import mapboxPolyline from '@mapbox/polyline';

/**
 * A ride that drives itself, for looking at the live page without a driver,
 * a rider, or a backend.
 *
 * It produces the same `RideLiveState` the admin endpoint streams, on the same
 * ~2.5s cadence, so the map, the glide, the sprite and the panel are all
 * exercised by the real components — only the source of the state is fake.
 */

// A drive down Guadalupe and out along the river. Real enough to look like a
// road on the map, which is what matters for judging the car's motion.
const ROUTE = [
  [30.29268, -97.74221],
  [30.28857, -97.74216],
  [30.28419, -97.74206],
  [30.28073, -97.74198],
  [30.27829, -97.74195],
  [30.27554, -97.74188],
  [30.27288, -97.74184],
  [30.27032, -97.74181],
  [30.26841, -97.74329],
  [30.26689, -97.74586],
  [30.26544, -97.74889],
  [30.26404, -97.75201],
  [30.26221, -97.75512],
];

/** Densifies the route so the car has somewhere to be between corners. */
const densify = (points, perLeg = 12) => {
  const out = [];
  points.forEach(([lat, lng], index) => {
    const next = points[index + 1];
    if (!next) {
      out.push({ lat, lng });
      return;
    }
    for (let step = 0; step < perLeg; step += 1) {
      const t = step / perLeg;
      out.push({ lat: lat + (next[0] - lat) * t, lng: lng + (next[1] - lng) * t });
    }
  });
  return out;
};

export const PATH = densify(ROUTE);

export const ROUTE_POLYLINE = mapboxPolyline.encode(
  PATH.map(({ lat, lng }) => [lat, lng]),
  6,
);

/** Compass bearing from one point to the next, in degrees clockwise from north. */
export const bearingBetween = (from, to) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLng = toRad(to.lng - from.lng);
  const y = Math.sin(dLng) * Math.cos(toRad(to.lat));
  const x =
    Math.cos(toRad(from.lat)) * Math.sin(toRad(to.lat)) -
    Math.sin(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

const PICKUP = { lat: ROUTE[0][0], lon: ROUTE[0][1], address: '2222 Guadalupe St' };
const DROPOFF = {
  lat: ROUTE[ROUTE.length - 1][0],
  lon: ROUTE[ROUTE.length - 1][1],
  address: 'Auditorium Shores',
};

/**
 * The phase the ride is in at `progress` (0..1 along the path), with the
 * display directives the api derives from it. Deliberately the same shape and
 * the same names, so the panel cannot pass here and fail on a real ride.
 */
const phaseFor = (progress) => {
  if (progress <= 0) {
    return {
      phase: 'pre_trip',
      showDriverLocation: false,
      showPickupEta: false,
      showDropoffEta: false,
      showRoute: false,
    };
  }
  if (progress < 0.18) {
    return {
      phase: 'driver_en_route_to_pickup',
      showDriverLocation: true,
      showPickupEta: true,
      showDropoffEta: false,
      showRoute: true,
    };
  }
  if (progress < 0.24) {
    return {
      phase: 'at_pickup',
      showDriverLocation: true,
      showPickupEta: false,
      showDropoffEta: false,
      showRoute: false,
    };
  }
  if (progress < 0.9) {
    return {
      phase: 'en_route_to_dropoff',
      showDriverLocation: true,
      showPickupEta: false,
      showDropoffEta: true,
      showRoute: true,
    };
  }
  if (progress < 1) {
    return { phase: 'arriving', showDriverLocation: true, showPickupEta: false, showDropoffEta: true, showRoute: true };
  }
  return {
    phase: 'completed',
    showDriverLocation: false,
    showPickupEta: false,
    showDropoffEta: false,
    showRoute: false,
  };
};

/**
 * The ride's state at `step` along the path — the object the SSE endpoint
 * would have sent.
 *
 * @param  {number} step - Index into {@link PATH}; clamped at both ends
 * @returns {object} - A RideLiveState
 */
export const stateAtStep = (step) => {
  const index = Math.max(0, Math.min(PATH.length - 1, step));
  const here = PATH[index];
  const next = PATH[Math.min(PATH.length - 1, index + 1)];
  const progress = index / (PATH.length - 1);
  const { phase, ...directives } = phaseFor(progress);
  const minutesLeft = Math.max(1, Math.round((1 - progress) * 18));

  return {
    rideId: 'sim-ride',
    tripId: 'sim-trip',
    phase,
    ...directives,
    rideStatus: progress >= 1 ? 'completed' : 'en_route',
    tripStatus: progress >= 1 ? 'completed' : 'in_progress',
    pickup: PICKUP,
    dropoff: DROPOFF,
    driverLocation: {
      lat: here.lat,
      lon: here.lng,
      bearing: bearingBetween(here, next),
      timestamp: new Date().toISOString(),
    },
    pickupEta:
      progress < 0.18
        ? { estimatedArrival: new Date(Date.now() + minutesLeft * 60000).toISOString(), minutesAway: minutesLeft }
        : undefined,
    dropoffEta: {
      estimatedArrival: new Date(Date.now() + minutesLeft * 60000).toISOString(),
      minutesAway: minutesLeft,
    },
    routePolyline: directives.showRoute ? ROUTE_POLYLINE : undefined,
    updatedAt: new Date().toISOString(),
  };
};
